import React, { createContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, IconButton, Snackbar, Alert, useTheme, useMediaQuery, Typography } from '@mui/material';
import { Menu } from '@mui/icons-material';
import { io } from 'socket.io-client';

// Components
import Login from './pages/Login';
import EmployeeDashboard from './pages/EmployeeDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Attendance from './pages/Attendance';
import Leave from './pages/Leave';
import LeaveRequests from './pages/LeaveRequests';
import Shifts from './pages/Shifts';
import Holidays from './pages/Holidays';
import EmployeeHolidays from './pages/EmployeeHolidays';
import HolidayApprovals from './pages/HolidayApprovals';
import HolidayCategories from './pages/HolidayCategories';
import HolidayCategoryManagement from './pages/HolidayCategoryManagement';
import Employees from './pages/Employees';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile';
import EmployeeReports from './pages/EmployeeReports';
import AdminReports from './pages/AdminReports';
import Calendar from './pages/Calendar';
import Settings from './pages/Settings';
import Tasks from './pages/Tasks';
import Payslip from './pages/Payslip';
import PayslipDetails from './pages/PayslipDetails';
import Companies from './pages/Companies';
import Sidebar, { DRAWER_WIDTH } from './components/Sidebar';
import Header from './components/Header';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Services
import { notificationsAPI } from './services/api';

// Sounds
import { playNotificationSound, playLoginSound, playLogoutSound, playBreakInSound, playBreakOutSound, playAlertSound } from './utils/sounds';

// Theme - Modern Dark with Premium Gold Accents
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D4AF37',
      light: '#F4D03F',
      dark: '#B8860B',
      contrastText: '#000000',
    },
    secondary: {
      main: '#C5A028',
      light: '#E6BE33',
      dark: '#8B7355',
    },
    success: {
      main: '#4CAF50',
      light: '#66BB6A',
      dark: '#388E3C',
    },
    warning: {
      main: '#FF9800',
      light: '#FFB74D',
      dark: '#F57C00',
    },
    error: {
      main: '#EF5350',
      light: '#E57373',
      dark: '#C62828',
    },
    info: {
      main: '#42A5F5',
      light: '#64B5F6',
      dark: '#1565C0',
    },
    background: {
      default: '#0A1929',
      paper: '#0F2744',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0BEC5',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h1: { 
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: { 
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: { 
      fontWeight: 700,
    },
    h4: { 
      fontWeight: 700,
    },
    h5: { 
      fontWeight: 600,
    },
    h6: { 
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
    body1: {
      fontWeight: 400,
    },
    body2: {
      fontWeight: 400,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: Array(25).fill('none').map((_, i) => {
    if (i === 0) return 'none';
    if (i === 1) return '0 2px 4px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)';
    if (i === 2) return '0 4px 8px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)';
    if (i === 3) return '0 6px 12px rgba(0,0,0,0.3), 0 3px 6px rgba(0,0,0,0.2)';
    if (i === 4) return '0 8px 16px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)';
    if (i === 5) return '0 10px 20px rgba(0,0,0,0.3), 0 5px 10px rgba(0,0,0,0.2)';
    if (i === 8) return '0 12px 24px rgba(0,0,0,0.4), 0 6px 12px rgba(0,0,0,0.3)';
    if (i === 12) return '0 16px 32px rgba(0,0,0,0.4), 0 8px 16px rgba(0,0,0,0.3)';
    if (i === 16) return '0 20px 40px rgba(0,0,0,0.4), 0 10px 20px rgba(0,0,0,0.3)';
    if (i === 24) return '0 24px 48px rgba(0,0,0,0.5), 0 12px 24px rgba(0,0,0,0.4)';
    return `0 ${Math.floor(i/2)}px ${i*2}px rgba(0,0,0,0.3), 0 ${Math.floor(i/3)}px ${i}px rgba(0,0,0,0.2)`;
  }),
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#D4AF37 #0A1929',
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: '#0A1929',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#D4AF37',
            borderRadius: 4,
            border: '2px solid #0A1929',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#F4D03F',
          },
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
        '@keyframes slideUp': {
          from: { opacity: 0, transform: 'translateY(20px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes glow': {
          '0%, 100%': { boxShadow: '0 0 5px rgba(212, 175, 55, 0.3)' },
          '50%': { boxShadow: '0 0 20px rgba(212, 175, 55, 0.6)' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12,
          padding: '10px 24px',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(212,175,55,0.3)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 50%, #D4AF37 100%)',
          backgroundSize: '200% 200%',
          animation: 'shimmer 3s ease infinite',
          color: '#000000',
          fontWeight: 700,
          '&:hover': {
            background: 'linear-gradient(135deg, #C5A028 0%, #E6BE33 50%, #C5A028 100%)',
            backgroundSize: '200% 200%',
          },
          '&.Mui-disabled': {
            background: 'rgba(212,175,55,0.3)',
            color: 'rgba(0,0,0,0.5)',
          },
        },
        outlinedPrimary: {
          border: '2px solid #D4AF37',
          color: '#D4AF37',
          '&:hover': {
            border: '2px solid #F4D03F',
            backgroundColor: 'rgba(212,175,55,0.08)',
          },
        },
        textPrimary: {
          color: '#D4AF37',
          '&:hover': {
            backgroundColor: 'rgba(212,175,55,0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgba(15, 39, 68, 0.9) 0%, rgba(10, 25, 41, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          borderRadius: 20,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(212,175,55,0.05)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 12px 48px rgba(0,0,0,0.4), 0 4px 16px rgba(212,175,55,0.1)',
            transform: 'translateY(-4px)',
            borderColor: 'rgba(212, 175, 55, 0.3)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'rgba(212,175,55,0.1)',
          height: 10,
        },
        bar: {
          borderRadius: 8,
          background: 'linear-gradient(90deg, #D4AF37 0%, #F4D03F 50%, #D4AF37 100%)',
          backgroundSize: '200% 200%',
          animation: 'shimmer 2s ease infinite',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
        },
        filled: {
          backgroundColor: 'rgba(212,175,55,0.15)',
          color: '#D4AF37',
        },
        outlined: {
          borderColor: 'rgba(212,175,55,0.4)',
          color: '#D4AF37',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(212,175,55,0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(212,175,55,0.15)',
            '&:hover': {
              backgroundColor: 'rgba(212,175,55,0.2)',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: 'rgba(212,175,55,0.25)',
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: 'rgba(212,175,55,0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#D4AF37',
              borderWidth: 2,
            },
            '&.Mui-focused': {
              boxShadow: '0 0 20px rgba(212,175,55,0.15)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(212,175,55,0.7)',
            '&.Mui-focused': {
              color: '#D4AF37',
            },
          },
          '& .MuiInputBase-input': {
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: '#D4AF37',
            backgroundColor: 'rgba(212,175,55,0.05)',
            borderBottom: '2px solid rgba(212,175,55,0.2)',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(212,175,55,0.05)',
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(212,175,55,0.08)',
          color: '#B0BEC5',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(135deg, #0F2744 0%, #0A1929 100%)',
          border: '1px solid rgba(212,175,55,0.2)',
          borderRadius: 24,
          boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
        },
        standardSuccess: {
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          color: '#66BB6A',
          border: '1px solid rgba(76, 175, 80, 0.3)',
        },
        standardError: {
          backgroundColor: 'rgba(239, 83, 80, 0.15)',
          color: '#EF5350',
          border: '1px solid rgba(239, 83, 80, 0.3)',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          color: '#FFB74D',
          border: '1px solid rgba(255, 152, 0, 0.3)',
        },
        standardInfo: {
          backgroundColor: 'rgba(66, 165, 245, 0.15)',
          color: '#64B5F6',
          border: '1px solid rgba(66, 165, 245, 0.3)',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0c132f',
          border: '1px solid rgba(212,175,55,0.3)',
          color: '#D4AF37',
          borderRadius: 8,
          fontSize: '0.75rem',
          padding: '8px 12px',
        },
        arrow: {
          color: '#1A3A2A',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: '2px solid rgba(212,175,55,0.3)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(212,175,55,0.15)',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: '#D4AF37',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#F4D03F',
          },
        },
      },
    },
  },
});

// Socket context
const SocketContext = createContext(null);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A1929 0%, #0F2744 100%)',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ 
              color: '#D4AF37',
              mb: 3,
            }} 
          />
          <Typography sx={{ color: '#D4AF37', fontWeight: 600, letterSpacing: 2 }}>
            LOADING...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Router component that decides which Reports component to show
const ReportsRouter = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'hr';
  
  if (isAdmin) {
    return <AdminReports />;
  }
  return <EmployeeReports />;
};

// Main Layout Component with Sidebar
const Layout = ({ children, unreadCount }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  if (location.pathname === '/login') {
    return <>{children}</>;
  }

  const SidebarToggleButton = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 80,
        left: sidebarOpen ? '280px' : 20,
        zIndex: 1200,
        transition: 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <IconButton
        onClick={toggleSidebar}
        sx={{
          background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
          color: '#000',
          width: 44,
          height: 44,
          boxShadow: '0 4px 14px rgba(212,175,55,0.4)',
          border: '2px solid rgba(212,175,55,0.5)',
          display: sidebarOpen && isMobile ? 'flex' : (sidebarOpen && !isMobile ? 'none' : 'flex'),
          opacity: sidebarOpen && !isMobile ? 0 : 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(212,175,55,0.5)',
            transform: 'scale(1.05)',
          },
        }}
      >
        <Menu />
      </IconButton>
    </Box>
  );

  return (
       <Box sx={{ 
         display: '', 
         minHeight: '100vh', 
         bgcolor: '#0A1929'
       }}>
      <Header 
        onMenuClick={toggleSidebar}
        onNotificationsClick={() => navigate('/notifications')}
        onProfileClick={() => navigate('/profile')}
        unreadCount={unreadCount}
      />
      <Sidebar 
        open={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        onToggle={toggleSidebar}
      />
      <SidebarToggleButton />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          pt: '72px',
          ml: isMobile ? 0 : (sidebarOpen ? `280px` : 0),
          transition: 'margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          bgcolor: 'transparent',
          minHeight: '100vh',
          position: 'relative',
          zIndex: 1,
          '& > *': {
            animation: 'slideUp 0.5s ease forwards',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// App Routes
const AppRoutes = ({ unreadCount }) => {
  const { user } = useAuth();

  return (
    <Layout unreadCount={unreadCount}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        
        {/* Employee Routes */}
        <Route path="/" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <EmployeeDashboard />
          </ProtectedRoute>
        } />
        <Route path="/attendance" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <Attendance />
          </ProtectedRoute>
        } />
        <Route path="/admin/leave-requests" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <LeaveRequests />
          </ProtectedRoute>
        } />
        <Route path="/leave" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <Leave />
          </ProtectedRoute>
        } />
        <Route path="/my-holidays" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <EmployeeHolidays />
          </ProtectedRoute>
        } />
        <Route path="/holiday-categories" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <HolidayCategories />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <Notifications />
          </ProtectedRoute>
        } />
        <Route path="/payslip" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <Payslip />
          </ProtectedRoute>
        } />

        {/* Admin/HR Routes */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/employees" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <Employees />
          </ProtectedRoute>
        } />
        <Route path="/admin/shifts" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <Shifts />
          </ProtectedRoute>
        } />
        <Route path="/admin/holidays" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <Holidays />
          </ProtectedRoute>
        } />
        <Route path="/admin/holiday-approvals" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <HolidayApprovals />
          </ProtectedRoute>
        } />
        <Route path="/admin/holiday-categories" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <HolidayCategoryManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/companies" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <Companies />
          </ProtectedRoute>
        } />
        <Route path="/admin/payslip-details" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <PayslipDetails />
          </ProtectedRoute>
        } />

        {/* Reports */}
        <Route path="/reports" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <ReportsRouter />
          </ProtectedRoute>
        } />
        
        {/* Calendar */}
        <Route path="/calendar" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <Calendar />
          </ProtectedRoute>
        } />
        
        {/* Settings */}
        <Route path="/settings" element={
          <ProtectedRoute allowedRoles={['hr', 'admin']}>
            <Settings />
          </ProtectedRoute>
        } />
        
        {/* Tasks */}
        <Route path="/tasks" element={
          <ProtectedRoute allowedRoles={['employee', 'hr', 'admin']}>
            <Tasks />
          </ProtectedRoute>
        } />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

// App Content Component
const AppContent = () => {
  const [socket, setSocket] = useState(null);
  const [notification, setNotification] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      if (response.data.success) {
        setUnreadCount(response.data.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:5076', {
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('Socket connected');
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    newSocket.on('new_notification', (data) => {
      console.log('New notification received:', data);
      setNotification(data);
      setUnreadCount(prev => prev + 1);
      
      if (data.priority === 'urgent' || data.priority === 'high') {
        playAlertSound();
      } else {
        playNotificationSound();
      }
    });

    newSocket.on(`user_${user?.id}`, (data) => {
      console.log('User notification received:', data);
      setNotification(data);
      playNotificationSound();
    });

    newSocket.on(`department_${user?.department}`, (data) => {
      console.log('Department notification received:', data);
      setNotification(data);
      playNotificationSound();
    });

    let previousStatus = null;
    
    newSocket.on('employee_status', (data) => {
      console.log('Employee status:', data);
      
      if (data.status === 'online') {
        if (previousStatus === 'on_break') {
          playBreakOutSound();
        } else if (previousStatus === 'offline') {
          playLoginSound();
        }
      } else if (data.status === 'offline') {
        playLogoutSound();
      } else if (data.status === 'on_break') {
        playBreakInSound();
      }
      
      previousStatus = data.status;
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [user]);

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleNotificationClick = () => {
    setNotification(null);
    navigate('/notifications');
  };

  return (
    <>
      <SocketContext.Provider value={socket}>
        <AppRoutes unreadCount={unreadCount} />
      </SocketContext.Provider>
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification}
          severity={notification?.priority === 'urgent' || notification?.priority === 'high' ? 'error' : 'info'}
          sx={{ 
            width: '100%', 
            cursor: 'pointer',
            '&:hover': {
              transform: 'scale(1.02)',
              transition: 'transform 0.2s ease',
            },
          }}
          onClick={handleNotificationClick}
        >
          <strong>{notification?.title}</strong>
          <br />
          {notification?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// Main App Component
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export { SocketContext };
export default App;